import React, { useState } from 'react';

const layers = [
  {
    id: 'cicd',
    label: 'CI/CD Pipeline',
    color: 'border-red-400/40 bg-red-950/20',
    titleColor: 'text-stone-200',
    nodes: [
      { name: 'GitHub', desc: 'Source control & webhook trigger', icon: '🐙' },
      { name: 'Jenkins', desc: 'Build → Test → Deploy pipeline', icon: '🔧' },
      { name: 'Jest / RTL', desc: 'React unit & integration tests', icon: '✅' },
      { name: 'pytest', desc: 'FastAPI unit tests', icon: '✅' },
    ],
  },
  {
    id: 'routing',
    label: 'Routing & Proxy',
    color: 'border-orange-400/40 bg-orange-950/20',
    titleColor: 'text-orange-100',
    nodes: [
      { name: 'Nginx Proxy Manager', desc: 'SSL termination, domain routing, reverse proxy', icon: '🌐' },
    ],
  },
  {
    id: 'services',
    label: 'Microservices',
    color: 'border-blue-400/40 bg-blue-950/20',
    titleColor: 'text-blue-100',
    nodes: [
      { name: 'React UI', desc: 'Portfolio + Live AI demo', icon: '⚛️' },
      { name: 'Spring Boot', desc: 'Java REST API — /api/spring', icon: '🍃' },
      { name: 'FastAPI', desc: 'Python REST API — /api/fastapi', icon: '⚡' },
      { name: 'AI Service (aip)', desc: 'Intent extraction via qwen2.5:0.5b', icon: '🤖' },
    ],
  },
  {
    id: 'ai',
    label: 'AI Layer',
    color: 'border-purple-400/40 bg-purple-950/20',
    titleColor: 'text-purple-100',
    nodes: [
      { name: 'Ollama', desc: 'Local LLM runtime — /api/generate', icon: '🧠' },
      { name: 'qwen2.5:0.5b', desc: '400MB model, ~5s inference on Pi 4', icon: '💬' },
      { name: 'llama-3.1', desc: '8 billion paramters, on Pi 4', icon: '💬' },
    ],
  },
  {
    id: 'data',
    label: 'Data Layer',
    color: 'border-indigo-400/40 bg-indigo-950/20',
    titleColor: 'text-indigo-100',
    nodes: [
      { name: 'PostgreSQL', desc: 'Primary database', icon: '🐘' },
      { name: 'Redis', desc: 'Cache & session store', icon: '⚡' },
    ],
  },
  {
    id: 'infra',
    label: 'Infrastructure',
    color: 'border-teal-400/40 bg-teal-950/20',
    titleColor: 'text-teal-100',
    nodes: [
      { name: 'Docker Compose', desc: 'Container orchestration, shared-net network', icon: '🐳' },
      { name: 'Portainer', desc: 'Container management UI', icon: '📊' },
      { name: 'Raspberry Pi 4', desc: '4GB RAM — runs the entire stack', icon: '🔴' },
    ],
  },
];

export default function Architecture() {
  const [active, setActive] = useState(null);

  return (
    <section id="architecture" className="py-24 px-6 max-w-5xl mx-auto">
      <p className="text-accent font-body text-sm tracking-[0.2em] uppercase mb-4">Architecture</p>
      <h2 className="font-display text-4xl text-ink mb-4">How it's built</h2>
      <p className="font-body text-muted text-sm mb-12 max-w-lg">
        Every service runs in Docker on a single Raspberry Pi 4. Click a layer to explore.
      </p>

      <div className="space-y-3">
        {layers.map((layer) => (
          <div
            key={layer.id}
            className={`border rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${layer.color} ${active === layer.id ? 'ring-1 ring-accent/30' : ''}`}
            onClick={() => setActive(active === layer.id ? null : layer.id)}
          >
            {/* Layer header */}
            <div className="flex items-center justify-between px-6 py-4">
              <p className={`font-body text-sm font-medium tracking-wide ${layer.titleColor}`}>
                {layer.label}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  {layer.nodes.map(n => (
                    <span key={n.name} className="text-xs text-muted hidden sm:inline">{n.icon}</span>
                  ))}
                </div>
                <span className="text-muted text-xs">{active === layer.id ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* Expanded nodes */}
            {active === layer.id && (
              <div className="px-6 pb-5 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {layer.nodes.map(node => (
                  <div key={node.name} className="bg-ink/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span>{node.icon}</span>
                      <p className="font-body text-sm text-cream font-medium">{node.name}</p>
                    </div>
                    <p className="font-body text-xs text-muted leading-relaxed">{node.desc}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Flow diagram */}
      <div className="mt-12 border border-ink/10 rounded-xl p-6">
        <p className="font-body text-xs text-muted uppercase tracking-widest mb-6">Request flow</p>
        <div className="flex flex-wrap items-center gap-2 font-body text-xs text-muted">
          {[
            { label: 'Browser', color: 'bg-cream text-ink' },
            '→',
            { label: 'Nginx Proxy', color: 'bg-orange-900/60 text-orange-300' },
            '→',
            { label: 'React UI / FastAPI / Spring', color: 'bg-blue-900/60 text-blue-300' },
            '→',
            { label: 'Ollama AI', color: 'bg-purple-900/60 text-purple-300' },
            '→',
            { label: 'PostgreSQL', color: 'bg-indigo-900/60 text-indigo-300' },
          ].map((item, i) =>
            typeof item === 'string'
              ? <span key={i} className="text-accent font-bold">{item}</span>
              : <span key={i} className={`px-3 py-1.5 rounded-lg text-xs ${item.color}`}>{item.label}</span>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-ink/10">
          <p className="font-body text-xs text-muted uppercase tracking-widest mb-4">CI/CD flow</p>
          <div className="flex flex-wrap items-center gap-2 font-body text-xs text-muted">
            {[
              { label: 'git push', color: 'bg-gray-800 text-gray-300' },
              '→',
              { label: 'GitHub Webhook', color: 'bg-gray-700 text-gray-300' },
              '→',
              { label: 'Jenkins', color: 'bg-red-900/60 text-red-300' },
              '→',
              { label: 'Run Tests', color: 'bg-yellow-900/60 text-yellow-300' },
              '→',
              { label: 'Docker Build', color: 'bg-sky-900/60 text-sky-300' },
              '→',
              { label: 'Deploy', color: 'bg-green-900/60 text-green-300' },
            ].map((item, i) =>
              typeof item === 'string'
                ? <span key={i} className="text-accent font-bold">{item}</span>
                : <span key={i} className={`px-3 py-1.5 rounded-lg text-xs ${item.color}`}>{item.label}</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
